import { motion } from "framer-motion";
import {
  Sparkles,
  Wrench,
  ShieldCheck,
  UsersRound,
  Home,
  Hammer,
  Building2,
  ArrowUpRight,
  Check,
} from "lucide-react";
import {
  CONSULTANCY_SERVICES,
  CLEANING_SERVICES,
} from "@/lib/site";

const ICONS = {
  C01: Sparkles,
  C02: Wrench,
  C03: ShieldCheck,
  C04: UsersRound,
  K01: Home,
  K02: Hammer,
  K03: Building2,
};

function ServiceCard({ code, title, body, bullets, accent = false, span = "" }) {
  const Icon = ICONS[code] || Sparkles;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      data-testid={`service-card-${code}`}
      className={`group relative p-7 sm:p-8 border border-white/10 bg-[#0A0A0A] hover:border-[#FBBF24]/60 transition-colors flex flex-col ${
        bullets ? "justify-start" : "justify-between"
      } min-h-[220px] ${span}`}
    >
      <div className="flex items-start justify-between">
        <Icon
          className={`w-7 h-7 ${
            accent ? "text-[#FBBF24]" : "text-white"
          } group-hover:text-[#FBBF24] transition-colors`}
          strokeWidth={1.6}
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">
          {code}
        </span>
      </div>
      <div className="mt-10">
        <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-white">
          {title}
        </h3>
        <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{body}</p>
        {bullets && bullets.length > 0 && (
          <ul
            data-testid={`service-card-${code}-bullets`}
            className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2"
          >
            {bullets.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-neutral-300"
              >
                <Check className="w-3.5 h-3.5 mt-1 text-[#FBBF24] shrink-0" strokeWidth={2.4} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ArrowUpRight className="absolute top-7 right-7 w-4 h-4 text-neutral-700 group-hover:text-[#FBBF24] opacity-0 group-hover:opacity-100 transition-all" />
    </motion.div>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-14 sm:mb-20">
          <div className="lg:col-span-5">
            <p className="tag-label mb-5">// Section 01 / Services</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-white leading-[1.05]">
              Two divisions.
              <br />
              <span className="text-[#FBBF24]">One accountable</span> team.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-neutral-400 text-base sm:text-lg leading-relaxed">
              We split our work into two clean verticals: <span className="text-white">Consultancy</span> for the strategy, audits and compliance — and a hands-on <span className="text-white">Cleaning</span> arm operating across the North West, providing office, school, end-of-tenancy and after-builders cleaning services. We focus on <span className="text-white">commercial, educational, property, and construction sector</span> clients.
            </p>
          </div>
        </div>

        {/* Consultancy */}
        <div className="mb-16">
          <div className="flex items-baseline justify-between mb-6">
            <h3
              data-testid="consultancy-heading"
              className="font-display text-2xl sm:text-3xl font-bold text-white"
            >
              <span className="text-[#FBBF24] font-mono text-sm align-top mr-3">
                01 /
              </span>
              Consultancy
            </h3>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
              4 disciplines
            </span>
          </div>

          {/* 2x2 grid — each card is a discipline with its sub-services */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/10">
            {CONSULTANCY_SERVICES.map((s, i) => (
              <ServiceCard
                key={s.code}
                {...s}
                accent={i === 0}
              />
            ))}
          </div>
        </div>

        {/* Cleaning */}
        <div>
          <div className="flex items-baseline justify-between mb-6">
            <h3
              data-testid="cleaning-heading"
              className="font-display text-2xl sm:text-3xl font-bold text-white"
            >
              <span className="text-[#FBBF24] font-mono text-sm align-top mr-3">
                02 /
              </span>
              Cleaning Services
            </h3>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
              Specialist · Commercial · Educational
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/10">
            {CLEANING_SERVICES.map((s) => (
              <ServiceCard key={s.code} {...s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
